import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UserSquare2, Plus, Edit2, Archive, X, Save } from "lucide-react";

const MANAGE_ROLES = new Set(["it_admin", "legal_manager", "general_counsel"]);
const ROLE_LABELS = ["GC", "Attorney", "Analyst", "Reviewer", "Records", "Compliance", "IT", "Purchasing"];

export default function ReviewerSlotsAdmin() {
  const { user } = useAuth();
  const canManage = user && MANAGE_ROLES.has(user.role);
  const [slots, setSlots] = useState([]);
  const [editing, setEditing] = useState(null); // slot object or {new:true}
  const [includeInactive, setIncludeInactive] = useState(false);

  const load = () => {
    api.get(`/reviewer-slots?include_inactive=${includeInactive}`)
       .then(({ data }) => setSlots(data || []))
       .catch(e => toast.error(e?.response?.data?.detail || "Failed to load"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive]);

  const archive = async (s) => {
    if (!window.confirm(`Archive "${s.role_label} — ${s.name}"? Existing dates stay on contracts but the column disappears from trackers.`)) return;
    try { await api.delete(`/reviewer-slots/${s.id}`); toast.success("Archived"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6" data-testid="reviewer-slots-page">
      <div className="mb-6 flex items-start">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Administration · Tracker</div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UserSquare2 className="w-6 h-6 text-[#005289]"/> Reviewer Roster
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Named reviewer slots surface as columns in every contract tracker (Dashboard, My Queue, Pipeline, Renewals, Purchasing).
            Adding or renaming a slot updates every list instantly. Reviewers can be assigned to any Legal role — the label you enter here appears verbatim on trackers and audit logs.
          </p>
        </div>
        {canManage ? (
          <button onClick={() => setEditing({ new: true, name: "", role_label: "Attorney", sort_order: 100, description: "" })}
                  data-testid="rs-add-btn"
                  className="ml-auto h-9 px-3 text-xs font-semibold bg-[#005289] text-white rounded flex items-center gap-1 hover:opacity-90">
            <Plus className="w-3 h-3"/> Add Reviewer Slot
          </button>
        ) : null}
      </div>

      <label className="text-xs flex items-center gap-2 mb-3">
        <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)}/>
        Show archived slots
      </label>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="rs-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2 w-24">Order</th>
              <th className="text-left px-4 py-2 w-32">Role</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Description</th>
              <th className="text-left px-4 py-2 w-24">Status</th>
              <th className="text-right px-4 py-2 w-40">Actions</th>
            </tr>
          </thead>
          <tbody>
            {slots.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center text-slate-400 text-sm">No slots yet.</td></tr>
            ) : null}
            {slots.map(s => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`rs-row-${s.id}`}>
                <td className="px-4 py-2 font-mono text-xs">{s.sort_order}</td>
                <td className="px-4 py-2">
                  <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-600 rounded px-2 py-0.5">{s.role_label}</span>
                </td>
                <td className="px-4 py-2 font-semibold">{s.name}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{s.description || "—"}</td>
                <td className="px-4 py-2">
                  {s.active ? <span className="text-[10px] text-green-700">Active</span> : <span className="text-[10px] text-slate-400">Archived</span>}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {canManage ? (
                    <>
                      <button onClick={() => setEditing(s)} data-testid={`rs-edit-${s.id}`} className="h-7 px-2 text-xs border border-slate-200 rounded hover:bg-slate-100 inline-flex items-center gap-1 mr-1">
                        <Edit2 className="w-3 h-3"/> Edit
                      </button>
                      {s.active ? (
                        <button onClick={() => archive(s)} data-testid={`rs-archive-${s.id}`} className="h-7 px-2 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 inline-flex items-center gap-1">
                          <Archive className="w-3 h-3"/> Archive
                        </button>
                      ) : null}
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing ? <SlotDialog slot={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/> : null}
    </div>
  );
}

function SlotDialog({ slot, onClose, onSaved }) {
  const isNew = !!slot.new;
  const [f, setF] = useState({
    name: slot.name || "",
    role_label: slot.role_label || "Attorney",
    sort_order: slot.sort_order ?? 100,
    description: slot.description || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      if (isNew) { await api.post("/reviewer-slots", f); toast.success("Slot created"); }
      else       { await api.patch(`/reviewer-slots/${slot.id}`, f); toast.success("Slot updated"); }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center" onClick={onClose} data-testid="rs-dialog">
      <div className="bg-white rounded-md w-[460px] max-w-[90vw]" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <UserSquare2 className="w-4 h-4 text-[#005289]"/>
          <div className="font-bold text-slate-900">{isNew ? "New reviewer slot" : "Edit slot"}</div>
          <button onClick={onClose} className="ml-auto text-slate-400"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs block col-span-2">
              <span className="font-bold text-slate-700 block mb-1">Reviewer name *</span>
              <input value={f.name} onChange={e => setF({...f, name: e.target.value})} data-testid="rs-dialog-name"
                     className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md" placeholder="e.g., Sarahi Ruiz"/>
            </label>
            <label className="text-xs">
              <span className="font-bold text-slate-700 block mb-1">Role label</span>
              <select value={f.role_label} onChange={e => setF({...f, role_label: e.target.value})} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md">
                {ROLE_LABELS.map(r => <option key={r}>{r}</option>)}
              </select>
            </label>
            <label className="text-xs">
              <span className="font-bold text-slate-700 block mb-1">Sort order</span>
              <input type="number" value={f.sort_order} onChange={e => setF({...f, sort_order: parseInt(e.target.value) || 100})}
                     className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md"/>
            </label>
          </div>
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Description</span>
            <textarea rows={2} value={f.description} onChange={e => setF({...f, description: e.target.value})} className="w-full p-2 text-sm border border-slate-200 rounded-md" placeholder="Optional — what this reviewer covers"/>
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="rs-dialog-save" className="h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white flex items-center gap-1 disabled:opacity-60">
            <Save className="w-3 h-3"/> {busy ? "Saving..." : (isNew ? "Create" : "Update")}
          </button>
        </div>
      </div>
    </div>
  );
}
